import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { useDispatch, useSelector } from 'react-redux';
import { Panel } from 'primereact/panel';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import moment from 'moment';

import appActions from '../../../appActions';
import InCompleteIcon from '../../../components/InCompleteIcon';
import CompleteIcon from '../../../components/CompleteIcon';
import TaskDetail from './TaskDetail';
import TaskFileUploadForm from './TaskFileUploadForm';
import RenderTooltipTemplate from '../../../components/renderTooltipTemplate';
import { getWindowDimensions, dateTimeFormat } from '../../../utils/mics';

const TaskPanel = ({ taskGroup, userRole }) => {

    const dispatch = useDispatch();

    const { width } = getWindowDimensions();
    
    const task = useSelector((state) => state.task.task);
    const [visible, setVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [completionVisible, setCompletionVisible] = useState(false);
    const [taskRecords, setTaskRecords] = useState([]);

    const { title, tasks } = taskGroup;

    const taskTag = useSelector(state => state.task.taskTag);
    const siteGroupOptions = useSelector((state) => state.common.siteGroupList);
    const storeOtions = useSelector((state) => state.common.storeList);

    const fetchTaskTagComments = useCallback((record) => {
        if(record) {
            dispatch(appActions.TASKTAG_GET_ALL_REQUEST({taskId: record.id, tagType: "Comment"}));
        }
    },[]);

    useEffect(() => {
        if (task.id) {
            setVisible(false);
        }
    }, [task]);

    useEffect(() => {
        if (tasks && siteGroupOptions && storeOtions) {
            const updatedTasks = tasks.map(task => {
                const assignedTo = task.assignedTo.split(",");
                // Initialize an array to hold descriptions
                const descriptions = [];
                
                // Check for matches in siteGroupOptions
                assignedTo.forEach(type => {
                    const matchSiteGroup = siteGroupOptions.find(option => option.siteCode === type.trim());
                    if (matchSiteGroup) {
                        descriptions.push(matchSiteGroup.siteName);
                    }
                    else {
                        const matchSiteName = storeOtions.find(option => option.siteId === Number(type.trim()));
                        if (matchSiteName) {
                            descriptions.push(matchSiteName.title);
                        }
                        else {
                            descriptions.push(type);
                        }
                    }

                });

                // Join the descriptions into a single string or keep as an array if needed
                const siteGroupTypeDesc = descriptions.join(", ") || "";

                // Return the updated task with the new description
                return {
                    ...task,
                    siteGroupTypeDesc
                };
            })

            setTaskRecords(updatedTasks);
        }
    }, [tasks, siteGroupOptions, storeOtions]);

    useEffect(() => {
        if(taskTag) {
            fetchTaskTagComments(selectedRecord);
            dispatch(appActions.TASKTAG_GET_ALL_FAILURE());
        }
    }, [ taskTag, dispatch, fetchTaskTagComments, selectedRecord ])

    // --- Templates ---
    const headerTemplate = (options) => (
        <div className={`${options.className} justify-content-space-between`}>
            <div className="flex align-items-center gap-2">
                {options.togglerElement}
                <span className="font-bold">
                    {title} ({taskRecords.length})
                </span>
            </div>
        </div>
    );

    const statusIconTemplate = (rowData) => (
        rowData.status === 'COMPLETED' ? <CompleteIcon /> : <InCompleteIcon />
    );

    const descriptionTemplate = (rowData) => (
        <span>
            <b>Note:</b> {rowData.description}
        </span>
    );

    const assignedTemplate = (field, label) => (rowData) => (
        <span>
            <span className="text-sm font-bold">{label}:</span>
            <br /> {rowData[field]}
        </span>
    );

    const taskDateTemplate = (rowData) => {
        const formattedDate = moment(rowData.taskDateTime, 'DD-MM-YYYY HH:mm');
        const dateString = rowData.repeatType === 'N' 
            ? formattedDate.format('DD MMMM YYYY, hA').toLowerCase()
            : `Daily, ${formattedDate.format('hA').toLowerCase()}`;
        return (
            <div className="flex">
                <i className="pi pi-calendar mr-2" style={{ fontSize: '1rem' }} />
                <span>{dateString}</span>
            </div>
        );
    };

    const impactTypeTemplate = (rowData) => (
        <div className="flex">
            <span>{rowData.impact}</span>
        </div>
    );

    const taskDateTimeTemplate = (rowData) => {
        return (
            <div className="flex">
                <i className="pi pi-calendar mr-2" style={{ fontSize: '1rem' }} />
                <span>{rowData.taskDateTime}</span>
            </div>
        );
    };

    const dueDateTimeTemplate = (rowData) => {
        return (
            <div className="flex">
                <i className="pi pi-calendar mr-2" style={{ fontSize: '1rem' }} />
                <span>{rowData.dueDateTime}</span>
            </div>
        );
    };

    const assignTypeTemplate = (rowData) => (
        <div className="flex">
            <span>{rowData.assignType}</span>
        </div>
    );

    const taskTypeTemplate = (rowData) => (
        <div className="flex">
            {/* <span>{rowData.taskType}</span> */}
            <span>{rowData.siteGroupTypeDesc}</span>
        </div>
    );

    const statusTemplate = (rowData) => (
        <div className={`text-xs status ${rowData.status?.replace(' ', '-').toLowerCase()}`}>
            {rowData.status}
        </div>
    );

    const priorityTemplate = (rowData) => (
        <div className="flex">
            <Badge
                className="mr-2"
                value="!"
                severity={getSeverity(rowData.priority)}
            />
            <span>Priority {rowData.priority}</span>
        </div>
    );

    const viewTemplate = (rowData) => (
        <div className="flex align-items-center gap-10">
            <span className="underline cursor-pointer font-bold" onClick={() => handleView(rowData)}>
                View
            </span>
             {rowData.status !== 'DELETED' && (
            <span className="underline cursor-pointer font-bold ms-2" onClick={() => handleCopy(rowData)}>
                Delete
            </span>
            )}
            {/* {rowData.status !== 'COMPLETED' && rowData.status !== 'DELETED' && (
                <span className="underline cursor-pointer font-bold ms-2" onClick={() => handleEditForm(rowData)}>
                   Update
                </span>
            )}  */}
        </div>
    );
    

    const proofTemplate = (rowData) => (
        <span>
            <b>Proof:</b>
            <br />
            {rowData.attachmentRequired === 'Y' ? 'Yes' : 'No'}
        </span>
    );

    const actionTemplate = (rowData) => {
        const actions = {
            'NOT_STARTED': {
                label: 'Start task',
                icon: 'pi pi-caret-right',
                onClick: () => handleStartTask(rowData),
            },
            'IN_PROGRESS': {
                label: 'End task',
                icon: 'pi pi-stop',
                onClick: () => handleEndTask(rowData),
            },
            'DELETED': {
                label: 'Deleted',
                icon: 'pi pi-trash',
            }
        };

        const action = actions[rowData.status];
        return action ? rowData.status === 'DELETED' ? (
            <Button
                label={action.label}
                severity="danger"
                size="small"
                icon={action.icon}
            />
        ) : (
            <Button
                label={action.label}
                severity="basic"
                size="small"
                icon={action.icon}
                onClick={action.onClick}
            />
        ) : null;
    };

    // --- Handlers ---
    const handleView = (record) => {
        setVisible(true);
        setSelectedRecord(record);
        fetchTaskTagComments(record);
        setIsEditMode(false);
    };

    const handleCopy  = (record) => {

        if(record) {
            dispatch(appActions.TASK_COPY_REQUEST({taskId: record.id}));
        }

    };

    const handleEditForm = (record) => {
        setVisible(true);
        setSelectedRecord(record);
        setIsEditMode(true);
    };

    const handleStartTask = (task) => {
        const updatedTask = {
            ...task,
            startDateTime: moment().format(dateTimeFormat),
            status: 'IN_PROGRESS',
        };
        dispatch(appActions.TASK_UPDATE_REQUEST(updatedTask));
    };

    const handleEndTask = (task) => {
        if (task.attachmentRequired === 'Y') {
            setCompletionVisible(true);
        } else {
            const updatedTask = {
                ...task,
                endDateTime: moment().format(dateTimeFormat),
                status: 'COMPLETED',
            };
            dispatch(appActions.TASK_UPDATE_REQUEST(updatedTask));
        }
    };

    const getSeverity = (priority) => {
        switch (priority) {
            case 'Medium':
                return 'warning';
            case 'High':
                return 'danger';
            case 'Low':
                return 'info';
            default:
                return 'info';
        }
    };

    

    return (
        <>
            <Panel
                className="mb-4"
                headerTemplate={headerTemplate}
                toggleable
            >
                <DataTable
                    value={taskRecords}
                    columnResizeMode="fit"
                    size='small'
                    className="p-datatable-sm no-border-table"
                    style={{ width: (width - 260 )+'px'}}
                >
                    {/* <Column body={statusIconTemplate} /> */}
                    <Column field="subject" header="Subject" style={{ width: '150px', maxWidth : '150px' }}  
                      body={(taskRecords) => <RenderTooltipTemplate rowData={taskRecords} field='subject' header='Subject' />}/>
                    <Column body={impactTypeTemplate} header="Impact" style={{ width: '150px', maxWidth : '150px' }} />
                    <Column body={priorityTemplate} field="priority" header="Priority" />
                    <Column body={taskDateTimeTemplate} field="taskDateTime" header="Start Date Time" />
                    <Column body={dueDateTimeTemplate} field="dueDateTime" header="End Date Time" />
                    <Column body={assignTypeTemplate} header="Audience" />
                    {/* <Column body={taskTypeTemplate} header="Site Group" /> */}
                    <Column field="siteGroupTypeDesc" header="Site Group/Sites" style={{ width: '140px', maxWidth : '140px' }}  
                      body={(taskRecords) => <RenderTooltipTemplate rowData={taskRecords} field='siteGroupTypeDesc' header='Site Group/Sites' />}/>
                    <Column body={viewTemplate} style={{ width: '4em' }} header="Action" />
                    {/* <Column body={actionTemplate} style={{ width: '125px' }} header="Action" /> */}
                </DataTable>
            </Panel>
                    
            
            <Dialog
                header={isEditMode ? `Update Message: ${selectedRecord?.title}` : `View Message: ${selectedRecord?.title}`}
                visible={visible}
                style={{ width: '75vw' }}
                onHide={() => setVisible(false)}
            >
                {isEditMode ? (
                    <TaskFileUploadForm record={selectedRecord} userRole={userRole} hideDialog={() => setVisible(false)} />
                ) : (
                    <TaskDetail
                        record={selectedRecord}
                        changeEditForm={handleEditForm}
                    />
                )}
            </Dialog>

            <Dialog
                header=""
                visible={completionVisible}
                style={{ width: '300px' }}
                onHide={() => setCompletionVisible(false)}
            >
                <p className="m-0 text-center">
                    <b>Completion attachment is required.</b>
                    <br />
                    Please click ‘Update’ and upload attachment to complete the task.
                </p>
            </Dialog>
        </>
    );
};

export default TaskPanel;
